/**
 * The statistics as the screen shows them: who comes first, which two kids are compared, and
 * how numbers, dates and kinds of races read. Pure (tested without a DOM).
 */
import type {
  BestResult,
  Duel,
  PlayerStats,
  RaceSettings,
  RecentRace,
  StatsResponse,
} from '@cube-rush/shared';
import { DEFAULT_RACE_SETTINGS } from '@cube-rush/shared';
import { ru } from '../../i18n/ru';
import { formatRaceTime } from '../../util/format';
import { raceSummary } from '../race/raceSummary';
import { placeLabel } from '../results/resultRows';

/**
 * The asking player first, then the two of «Кто кого» (`rivals`, by their ids), then everybody
 * else by how much they raced (the family before a guest of one race) and their latest race.
 */
export function orderPlayers(
  players: readonly PlayerStats[],
  rivals: readonly string[] = [],
): PlayerStats[] {
  const rank = (player: PlayerStats) => (player.me ? 2 : rivals.includes(player.id) ? 1 : 0);
  return [...players].sort(
    (a, b) => rank(b) - rank(a) || b.races - a.races || b.lastRaceAt - a.lastRaceAt,
  );
}

/** Two players side by side and how their races together went (a = left, b = right). */
export interface Rivals {
  left: PlayerStats;
  right: PlayerStats;
  /** Races together, and how often each came out ahead (0 when they never met). */
  together: number;
  leftAhead: number;
  rightAhead: number;
}

/**
 * The two kids to compare: the asking player and whoever they raced most; without such a pair,
 * the two who raced each other most; without any duel, the two with the most races. Null with
 * fewer than two players.
 */
export function pickRivals(stats: StatsResponse): Rivals | null {
  const players = orderPlayers(stats.players);
  if (players.length < 2) return null;
  const byId = new Map(players.map((p) => [p.id, p]));
  const me = players.find((p) => p.me);
  const duels = [...stats.family.duels].sort((x, y) => y.races - x.races || y.lastAt - x.lastAt);
  const duel =
    (me && duels.find((d) => (d.a === me.id || d.b === me.id) && byId.has(d.a) && byId.has(d.b))) ??
    duels.find((d) => byId.has(d.a) && byId.has(d.b));
  if (duel) {
    // The asking player always on the left.
    const swap = me !== undefined && duel.b === me.id;
    return fromDuel(
      byId.get(swap ? duel.b : duel.a)!,
      byId.get(swap ? duel.a : duel.b)!,
      duel,
      swap,
    );
  }
  const [first, second] = [...players].sort(
    (a, b) => Number(b.me) - Number(a.me) || b.races - a.races,
  );
  return { left: first!, right: second!, together: 0, leftAhead: 0, rightAhead: 0 };
}

function fromDuel(left: PlayerStats, right: PlayerStats, duel: Duel, swapped: boolean): Rivals {
  return {
    left,
    right,
    together: duel.races,
    leftAhead: swapped ? duel.bAhead : duel.aAhead,
    rightAhead: swapped ? duel.aAhead : duel.bAhead,
  };
}

/**
 * «2 ч 15 мин», «45 мин», «меньше минуты»; from 10 hours on the hours alone («127 ч»: minutes
 * are noise by then, and the text stays short). `short` (the comparison's columns): «< 1 мин».
 */
export function formatPlayTime(ms: number, short = false): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return short ? ru.stats.lessThanMinuteShort : ru.stats.lessThanMinute;
  if (minutes < 60) return ru.stats.minutes(minutes);
  const hours = Math.floor(minutes / 60);
  return ru.stats.hours(hours, hours >= 10 ? 0 : minutes % 60);
}

/** The day of a race: «сегодня», «вчера», else «11 окт.» (in the server's time zone). */
export function dayLabel(at: number, now: number, timeZone: string): string {
  const day = (ms: number) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(ms);
  const that = day(at);
  if (that === day(now)) return ru.stats.today;
  if (that === day(now - 86_400_000)) return ru.stats.yesterday;
  return new Intl.DateTimeFormat('ru-RU', { timeZone, day: 'numeric', month: 'short' }).format(at);
}

/** A full date: «11 октября 2026 г.» (when the family started playing). */
export function dateLabel(at: number, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(at);
}

/** The kind of race as the room's card says it: «🏁 10 уровней · 😎 Средне». */
export function raceKind(race: Pick<RecentRace, 'format' | 'difficulty' | 'length'>): string {
  const settings: RaceSettings = {
    ...DEFAULT_RACE_SETTINGS,
    format: race.format,
    difficulty: race.difficulty,
    ...(race.format === 'levels' ? { levelCount: race.length } : { durationSec: race.length }),
  };
  return raceSummary(settings, true);
}

/** A best result's score: the fastest finish, or the most levels in the time. */
export function bestScore(best: BestResult): string {
  if (best.format === 'levels') return best.timeMs === null ? '—' : formatRaceTime(best.timeMs);
  return ru.results.levelsDone(best.levels);
}

/** One line of the latest races. */
export interface RecentRow {
  key: string;
  day: string;
  kind: string;
  place: string;
  /** Racers in it («один на трассе», «3 игрока»). */
  company: string;
  levels: string;
  deaths: number;
  time: string;
}

export function recentRows(
  races: readonly RecentRace[],
  now: number,
  timeZone: string,
): RecentRow[] {
  return races.map((race) => ({
    key: race.raceId,
    day: dayLabel(race.at, now, timeZone),
    kind: raceKind(race),
    // Alone there is nobody to beat: no medal, just whether the race was done.
    place: race.racers === 1 ? (race.place !== null ? '🏁' : '—') : placeLabel(race.place),
    company: race.racers === 1 ? ru.stats.solo : ru.stats.racers(race.racers),
    levels:
      race.format === 'levels'
        ? ru.results.levelsOf(race.levels, race.length)
        : ru.results.levelsDone(race.levels),
    deaths: race.deaths,
    time:
      race.timeMs !== null
        ? formatRaceTime(race.timeMs)
        : race.quit
          ? ru.stats.quit
          : ru.results.notFinished,
  }));
}

/** Share (0..1) of the left value in a pair, for the comparison bars (equal when both are 0). */
export function leftShare(left: number, right: number): number {
  const total = left + right;
  return total > 0 ? left / total : 0.5;
}
